import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LocationModal } from '../../components/LocationModal';
import { toast } from 'react-toastify';
import styles from './EventRegister.module.css';
import { 
  getEventDetails, 
  checkUserRegistration, 
  registerForEvent, 
  verifyAndRegisterForEvent,
  EventDetails
} from '../../services/eventRegistrationService';

interface EventDetailsProps {
  Events_ID: string;
  title: string;
  sub_title?: string;
  event_date: string;
  location_name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  status?: number;
}

const Event_register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  // Accept both 'verification_code' and 'code' parameters for backward compatibility
  const urlVerificationCode = searchParams.get('verification_code') || searchParams.get('code') || '';
  const urlEmail = searchParams.get('email') || '';
  const urlFirstName = searchParams.get('firstname') || '';
  
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [formData, setFormData] = useState({
    email: urlEmail,
    verification_code: urlVerificationCode,
    first_name: urlFirstName,
    last_name: '',
  });
  const [touched, setTouched] = useState({
    email: false,
    verification_code: false,
    first_name: false,
    last_name: false,
  });

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; // Empty is not invalid, just empty
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Show verification code input only if not provided via URL
  const showVerificationInput = !urlVerificationCode;

  useEffect(() => {
    // Load event details
    if (eventId) {
      fetchEventDetails(String(eventId));
    }

    // Auto-submit if all required data is in URL
    if (eventId && urlEmail && urlVerificationCode && urlFirstName) {
      // Wait a bit for event details to load, then auto-submit
      setTimeout(() => {
        submitRegistration();
      }, 500);
    }
  }, [eventId, urlEmail, urlVerificationCode, urlFirstName]);

  const fetchEventDetails = async (id: string) => {
    setIsLoadingDetails(true);
    setErrorMessage(null);
    try {
      const data = await getEventDetails(id);
      setEventDetails(data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      
      // Check if it's a 404 error
      if (error instanceof Error && error.message.includes('404')) {
        setErrorMessage('Event not found. The event you are looking for does not exist or has been removed.');
      } else if (error instanceof Error) {
        setErrorMessage(`Error loading event details: ${error.message}`);
      } else {
        setErrorMessage('Could not load event details. Please try again later.');
      }
      
      toast.error('Could not load event details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });
    
    // Mark field as touched when user starts typing
    if (value.length > 0) {
      setTouched({
        ...touched,
        [id]: true,
      });
    }
  };

  const handleBlur = (field: string) => {
    setTouched({
      ...touched,
      [field]: true,
    });
  };

  const submitRegistration = async () => {
    if (!eventId) return;
    
    // Mark all fields as touched when submitting
    setTouched({
      email: true,
      verification_code: true,
      first_name: true,
      last_name: true,
    });
    
    // Validate required fields
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // If verification code is required but not provided
    if (showVerificationInput && !formData.verification_code) {
      toast.error('Please enter the verification code');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await verifyAndRegisterForEvent(
        formData.email,
        formData.verification_code,
        formData.first_name,
        formData.last_name,
        eventId
      );

      // Check if user was already registered
      const alreadyRegistered = response.message?.toLowerCase().includes('already registered') || 
                                 response.message?.toLowerCase().includes('bereits angemeldet');
      
      setIsAlreadyRegistered(alreadyRegistered);
      setSuccessMessage(response.message || 'Registration completed successfully!');
      toast.success(response.message || 'Registration completed successfully!');
    } catch (error) {
      console.error('Error submitting registration:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
        toast.error(`Error: ${error.message}`);
      } else {
        setErrorMessage('An unexpected error occurred during registration');
        toast.error('An unexpected error occurred during registration');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render different UI states
  if (isLoadingDetails) {
    return (
      <div className={`container ${styles.loadingContainer}`}>
        <div className={`spinner-border text-primary ${styles.loadingSpinner}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3>Loading event details...</h3>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="container">
        <div className={`card p-4 shadow-sm ${styles.roleContainer} ${styles.errorContainer}`}>
          <div className={styles.errorIcon}>
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h4 className="mb-3">Unable to Load Event</h4>
          <div className="alert alert-danger">
            {errorMessage}
          </div>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => eventId && fetchEventDetails(String(eventId))}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!eventDetails) {
    return <div className="container">No event details available.</div>;
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={`card p-4 shadow-sm ${styles.roleContainer}`}>
        <div className={styles.roleHeader}>
          <h4 className="mb-0">Event Registration</h4>
          <h5 className={styles.eventTitle}>
            {eventDetails.title}
            {eventDetails.sub_title && (
              <i 
                className="bi bi-info-circle ms-2 text-primary" 
                onClick={() => setShowInfoModal(true)}
                style={{ cursor: 'pointer', fontSize: '1rem' }}
                title="Click for more information">
              </i>
            )}
          </h5>
        </div>
        
        <hr className={styles.headerDivider} />
        
        <h5 className={styles.eventDate}><strong>Event Date: </strong> {new Date(eventDetails.event_date).toLocaleDateString()} at {new Date(eventDetails.event_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}).toLowerCase()}</h5>
        
        {eventDetails.location_name && (
          <h5 className={styles.eventDate}>
            <strong>Event Location: </strong> 
            <span 
              style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
              data-bs-toggle="modal" 
              data-bs-target="#locationModal"
              data-location-name={eventDetails.location_name}
              data-street={eventDetails.street}
              data-house-number={eventDetails.house_number}
              data-postal-code={eventDetails.postal_code}
              data-city={eventDetails.city}
              data-country={eventDetails.country}
              title="Click to view location on map"
            >
              {eventDetails.location_name} | {eventDetails.street} {eventDetails.house_number}, 
              {eventDetails.postal_code} {eventDetails.city}, {eventDetails.country}
            </span>
          </h5>
        )}
        <br />

        {eventDetails.status === 1 ? ( // Assuming status 1 is "active"
          <div className={styles.formContainer}>
            {!successMessage ? (
              <div>
                <p className={styles.formDescription}>
                  Please fill in your details below to register for this event.
                </p>
                
                {/* Email Input */}
                <div className={styles.inputGroup}>
                  <label htmlFor="email" className={styles.inputLabel}>
                    <i className="bi bi-envelope me-2"></i>Email Address
                  </label>
                  <input 
                    type="text" 
                    id="email" 
                    className={`${styles.formInput} ${touched.email && formData.email && !isValidEmail(formData.email) ? styles.inputInvalid : ''}`}
                    placeholder="your.email@example.com" 
                    value={formData.email} 
                    onChange={handleInputChange}
                    disabled={isLoading || !!urlEmail}
                  />
                  {touched.email && formData.email && !isValidEmail(formData.email) && (
                    <small className={styles.errorText}>Please enter a valid email address (e.g., name@example.com)</small>
                  )}
                </div>

                {/* Verification Code - Only show if not in URL */}
                {showVerificationInput && (
                  <div className={styles.inputGroup}>
                    <label htmlFor="verification_code" className={styles.inputLabel}>
                      <i className="bi bi-shield-check me-2"></i>Verification Code
                    </label>
                    <input 
                      type="text" 
                      id="verification_code" 
                      className={styles.formInput}
                      placeholder="Enter your verification code" 
                      value={formData.verification_code} 
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                  </div>
                )}
                
                {/* Name Row */}
                <div className={styles.nameRow}>
                  {/* First Name */}
                  <div className={styles.inputGroup}>
                    <label htmlFor="first_name" className={styles.inputLabel}>
                      <i className="bi bi-person me-2"></i>First Name
                    </label>
                    <input 
                      type="text" 
                      id="first_name" 
                      className={styles.formInput}
                      placeholder="John" 
                      value={formData.first_name} 
                      onChange={handleInputChange}
                      disabled={isLoading || !!urlFirstName}
                    />
                  </div>

                  {/* Last Name */}
                  <div className={styles.inputGroup}>
                    <label htmlFor="last_name" className={styles.inputLabel}>
                      <i className="bi bi-person-fill me-2"></i>Last Name
                    </label>
                    <input 
                      type="text" 
                      id="last_name" 
                      className={styles.formInput}
                      placeholder="Doe" 
                      value={formData.last_name} 
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {errorMessage}
                  </div>
                )}

                {/* Submit Button */}
                <button 
                  className={`btn btn-primary ${styles.formButton}`}
                  onClick={submitRegistration}
                  disabled={isLoading || !formData.email || !isValidEmail(formData.email) || !formData.first_name || !formData.last_name || (showVerificationInput && !formData.verification_code)}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Registering...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Register for Event
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div>
                {isAlreadyRegistered ? (
                  <div className={styles.successContainer}>
                    <i className="bi bi-info-circle-fill" style={{ fontSize: '3rem', color: '#17a2b8', marginBottom: '1rem' }}></i>
                    <h3 style={{ color: '#17a2b8', marginBottom: '1rem' }}>Already Registered</h3>
                    <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.6', marginBottom: '0' }}>
                      You are already registered for <strong>{eventDetails.title}</strong>.
                      <br /><br />
                      We look forward to seeing you at the event!
                    </p>
                  </div>
                ) : (
                  <div className={styles.successContainer}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem', color: '#28a745', marginBottom: '1rem' }}></i>
                    <h3 style={{ color: '#28a745', marginBottom: '1rem' }}>Registration Successful!</h3>
                    <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.6', marginBottom: '0' }}>
                      You have successfully registered for <strong>{eventDetails.title}</strong>.
                      <br /><br />
                      You will receive a confirmation email with all event details and further information shortly.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={`d-flex justify-content-center ${styles.badgeContainer}`}>
            <span className="badge text-wrap text-center p-2 bg-warning">
              This event is no longer active.
            </span>
          </div>
        )}
      </div>

      {/* Include Location Modal */}
      <LocationModal />

      {/* Info Modal */}
      {eventDetails.sub_title && (
        <div className={`modal fade ${showInfoModal ? 'show' : ''}`} 
             style={{ display: showInfoModal ? 'block' : 'none' }}
             tabIndex={-1} 
             aria-labelledby="infoModalLabel" 
             aria-hidden={!showInfoModal}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="infoModalLabel">Event Information</h5>
                <button type="button" 
                        className="btn-close" 
                        onClick={() => setShowInfoModal(false)}
                        aria-label="Close">
                </button>
              </div>
              <div className="modal-body">
                {eventDetails.sub_title}
              </div>
              <div className="modal-footer">
                <button type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setShowInfoModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInfoModal && <div className="modal-backdrop fade show" onClick={() => setShowInfoModal(false)}></div>}
    </div>
  );
};

export default Event_register;
